/**
 * AnnouncementBanner — Phase 2
 *
 * Listens for 'broadcast_message' session_events and renders a dismissible
 * full-width banner above the tool. Facilitator-only; one-way.
 */

import { useEffect, useState } from 'react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { C } from '../theme/participantTokens';

interface AnnouncementBannerProps {
  sessionId: string;
  /** Set for a guest by link (S-8): the host's latest broadcast, from the guest's room read. Nothing is subscribed. */
  latest?: { id: string; message: string } | null;
}

export function AnnouncementBanner({ sessionId, latest }: AnnouncementBannerProps) {
  const [announcements, setAnnouncements] = useState<Array<{ id: string; message: string }>>([]);
  const controlled = latest !== undefined;
  const latestId = latest?.id ?? null;
  const latestMessage = latest?.message ?? '';

  // A guest's banner shows each new broadcast once.
  useEffect(() => {
    if (!controlled || !latestId) return;
    setAnnouncements((prev) => (prev.some((a) => a.id === latestId) ? prev : [...prev, { id: latestId, message: latestMessage }]));
  }, [controlled, latestId, latestMessage]);

  useEffect(() => {
    if (controlled) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const ch = supabase
      .channel(`announcements:${sessionId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
        payload => {
          const ev = payload.new as { id: string; event_type: string; payload: { message: string } };
          if (ev.event_type === 'broadcast_message') {
            setAnnouncements(prev => [...prev, { id: ev.id, message: ev.payload.message }]);
          }
        }
      )
      .subscribe();

    return () => { void supabase.removeChannel(ch); };
  }, [sessionId, controlled]);

  if (announcements.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
      {announcements.map(a => (
        <div
          key={a.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            background: 'rgba(212,160,23,0.08)',
            borderBottom: `1px solid rgba(212,160,23,0.2)`,
          }}
        >
          <span style={{ fontSize: '1rem', flexShrink: 0 }}>📣</span>
          <span
            style={{
              flex: 1,
              fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
              fontSize: '0.85rem',
              color: C.textPri,
            }}
          >
            {a.message}
          </span>
          <button
            type="button"
            onClick={() => setAnnouncements(prev => prev.filter(x => x.id !== a.id))}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: C.textDim,
              fontSize: '0.9rem',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
